import {useEffect, useRef, useState} from 'react';

/**
 * IntersectionObserver-driven reveal trigger. Returns a ref to attach to the
 * outer wrapper and a `triggered` boolean that flips true the first time the
 * element enters the viewport. Mirrors Nyx's `useScrollIntoView` in spirit —
 * web uses IO instead of manual measureLayout + scroll listeners.
 *
 * threshold: fraction of the element that must be visible (0..1). Default 0.1
 * so reveals fire early as the user scrolls toward them.
 *
 * onceMode: if true (default), once triggered stays true. If false, replays
 * each entry — matches Nyx's behavior of resetting on exit.
 */
export const useRevealOnView = ({threshold = 0.1, onceMode = true}: {threshold?: number; onceMode?: boolean} = {}): {
  ref: React.RefObject<HTMLDivElement>;
  triggered: boolean;
} => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [triggered, setTriggered] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') {
      setTriggered(true);
      return;
    }
    const io = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setTriggered(true);
            if (onceMode) io.disconnect();
          } else if (!onceMode) {
            setTriggered(false);
          }
        }
      },
      {threshold, rootMargin: '0px 0px -10% 0px'},
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold, onceMode]);

  return {ref, triggered};
};
